import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, functions } from "./firebase.js";
import { toast } from "react-toastify";
import { httpsCallable } from "firebase/functions";

function invoiceStatusLabel(invoice) {
  const state = invoice.displayStatus || invoice.status;
  if (state === "processing") return "Payment pending";
  if (state === "authentication_required") return "Action needed";
  if (state === "paid") return "Paid";
  if (state === "open") return "Due";
  if (state === "void") return "Void";
  if (state === "uncollectible") return "Uncollectible";
  return state || "Unknown";
}

function isProcessingInvoice(invoice) {
  return (invoice.displayStatus || invoice.status) === "processing";
}

function isPaidInvoice(invoice) {
  return (invoice.displayStatus || invoice.status) === "paid";
}

function isOutstandingInvoice(invoice) {
  if (isProcessingInvoice(invoice) || isPaidInvoice(invoice)) return false;
  if (invoice.isPayable === false) return false;
  const state = invoice.displayStatus || invoice.status;
  return state === "open" || state === "authentication_required";
}

function formatMoney(cents, currency = "usd") {
  const amount = (Number(cents) || 0) / 100;
  try {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    });
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function formatDate(unixSeconds) {
  if (!unixSeconds) return " - ";
  const ms = typeof unixSeconds === "number" && unixSeconds < 1e12 ? unixSeconds * 1000 : unixSeconds;
  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function daysUntilDue(dueUnix) {
  if (!dueUnix) return null;
  const due = new Date(dueUnix * 1000);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

function dueBadge(invoice) {
  if (isPaidInvoice(invoice) || isProcessingInvoice(invoice)) return null;
  const days = daysUntilDue(invoice.due_date);
  if (days === null) return { label: "No due date", tone: "muted" };
  if (days < 0) return { label: `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`, tone: "danger" };
  if (days === 0) return { label: "Due today", tone: "warn" };
  if (days <= 7) return { label: `Due in ${days} day${days === 1 ? "" : "s"}`, tone: "warn" };
  return { label: `Due in ${days} days`, tone: "ok" };
}

function statusHelpText(invoice) {
  const state = invoice.displayStatus || invoice.status;
  if (state === "processing") {
    return "Your payment was submitted and is clearing. Bank (ACH) payments often take 3-5 business days. You do not need to pay again unless it fails.";
  }
  if (state === "authentication_required") {
    return 'Your bank or card requires an extra verification step. Use "View full invoice" to complete authentication, or try another payment method.';
  }
  if (state === "paid") {
    return "This invoice is fully paid. Download the PDF for your records.";
  }
  if (state === "open") {
    return "This balance is open and payable. Pay with a saved method below or open the secure Stripe invoice page.";
  }
  if (invoice.isPayable === false) {
    return "This invoice is not currently payable online. Contact the HOA office if you believe this is an error.";
  }
  return "Status is provided by our billing system (Stripe) for full transparency.";
}

function normalizeTract(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^lot\s*/i, "");
}

function findLotForInvoice(invoice, lotList) {
  if (invoice.stripeCustomerId) {
    const byCustomer = lotList.find(
      (lot) =>
        lot.stripeCustomerId === invoice.stripeCustomerId ||
        lot.stripeCustomerIds?.includes(invoice.stripeCustomerId),
    );
    if (byCustomer) return byCustomer;
  }
  const tract = normalizeTract(invoice.lotNumber);
  return lotList.find((lot) => normalizeTract(lot.tract) === tract);
}

function paymentMethodsForInvoice(invoice, lotList) {
  const lot = findLotForInvoice(invoice, lotList);
  if (!lot) return [];
  if (!invoice.stripeCustomerId) return lot.paymentMethods || [];
  return (lot.paymentMethods || []).filter(
    (pm) => !pm.stripeCustomerId || pm.stripeCustomerId === invoice.stripeCustomerId,
  );
}

function pmLabel(pm) {
  if (!pm) return "Payment method";
  if (pm.type === "card") {
    return `${(pm.brand || "Card").toUpperCase()} •••• ${pm.last4 || "????"}`;
  }
  if (pm.type === "us_bank_account") {
    return `${pm.bank_name || "Bank"} •••• ${pm.last4 || "????"}`;
  }
  return pm.type || "Payment method";
}

function sumCents(invoices, field) {
  return invoices.reduce((sum, inv) => sum + (Number(inv[field]) || 0), 0);
}

const InvoicesPage = ({ isMobile }) => {
  const [invoices, setInvoices] = useState([]);
  const [paymentMethodsByLot, setPaymentMethodsByLot] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("paid"); // paid | all | void
  const [expandedId, setExpandedId] = useState(null);

  const [addingLoading, setAddingLoading] = useState(false);
  const [addingError, setAddingError] = useState(null);
  const [showLotSelector, setShowLotSelector] = useState(false);

  const [paymentConfirmation, setPaymentConfirmation] = useState(null);
  const [processingPaymentId, setProcessingPaymentId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoadError(null);
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(
        "https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/getInvoices",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ownerId: user.uid }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch invoices");
      }

      const data = await response.json();
      const filteredInvoices = (data.invoices || []).filter((invoice) => invoice.status !== "draft");
      setInvoices(filteredInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error.message);
      setLoadError(error.message);
      toast.error(`Error loading invoices: ${error.message}`);
    }
  }, []);

  const fetchPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    try {
      const getPaymentMethods = httpsCallable(functions, "getPaymentMethods");
      const result = await getPaymentMethods();
      setPaymentMethodsByLot(result.data.lots || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchInvoices(), fetchPaymentMethods()]);
    setRefreshing(false);
  }, [fetchInvoices, fetchPaymentMethods]);

  useEffect(() => {
    const loadData = async () => {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please log in to view invoices");
        setLoading(false);
        return;
      }
      await fetchInvoices();
      setLoading(false);
      fetchPaymentMethods();
    };

    loadData();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("setup_intent") && urlParams.get("redirect_status") === "succeeded") {
      toast.success("Payment method added successfully!");
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchPaymentMethods();
    }
  }, [fetchInvoices, fetchPaymentMethods]);

  const outstanding = useMemo(() => invoices.filter(isOutstandingInvoice), [invoices]);
  const processing = useMemo(() => invoices.filter(isProcessingInvoice), [invoices]);
  const paid = useMemo(() => invoices.filter(isPaidInvoice), [invoices]);

  const historyList = useMemo(() => {
    if (historyFilter === "paid") return paid;
    if (historyFilter === "void") {
      return invoices.filter((inv) => inv.stripeStatus === "void" || inv.status === "void");
    }
    return [...invoices].sort((a, b) => (b.created || 0) - (a.created || 0));
  }, [historyFilter, paid, invoices]);

  const totalOutstanding = useMemo(
    () => sumCents(outstanding, "amount_remaining") || sumCents(outstanding, "amount_due"),
    [outstanding],
  );
  const totalProcessing = useMemo(() => sumCents(processing, "amount_due"), [processing]);
  const totalPaid = useMemo(() => sumCents(paid, "amount_paid") || sumCents(paid, "amount_due"), [paid]);

  const nextDue = useMemo(() => {
    const withDue = outstanding
      .filter((inv) => inv.due_date)
      .sort((a, b) => a.due_date - b.due_date);
    return withDue[0] || null;
  }, [outstanding]);

  const handleSetDefault = async (lotId, paymentMethodId) => {
    try {
      const setDefault = httpsCallable(functions, "setDefaultPaymentMethod");
      await setDefault({ lotId, paymentMethodId });
      toast.success("Default payment method updated");
      fetchPaymentMethods();
    } catch (error) {
      console.error("Error setting default:", error);
      toast.error("Failed to set default payment method");
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm("Remove this payment method? You can add it again later.")) return;
    try {
      const detach = httpsCallable(functions, "detachPaymentMethod");
      await detach({ paymentMethodId });
      toast.success("Payment method removed");
      fetchPaymentMethods();
    } catch (error) {
      console.error("Error removing payment method:", error);
      toast.error("Failed to remove payment method");
    }
  };

  const handleAddNewPaymentMethod = () => {
    if (paymentMethodsByLot.length === 0) {
      toast.error("No lots found for your account. Contact the HOA office if this is unexpected.");
      return;
    }
    setAddingError(null);
    if (paymentMethodsByLot.length === 1) {
      startAddPaymentMethodForLot(paymentMethodsByLot[0].lotId);
    } else {
      setShowLotSelector(true);
    }
  };

  const startAddPaymentMethodForLot = async (lotId) => {
    setShowLotSelector(false);
    setAddingError(null);
    setAddingLoading(true);
    try {
      const createSetupIntent = httpsCallable(functions, "createSetupIntent");
      const result = await createSetupIntent({ lotId });
      const clientSecret = result.data.clientSecret;
      if (!clientSecret) throw new Error("No client secret returned");
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `https://checkout.stripe.com/setup?setup_intent_client_secret=${clientSecret}&return_url=${returnUrl}`;
    } catch (error) {
      console.error("Error starting payment method setup:", error);
      setAddingError("Failed to start payment method setup. Please try again.");
      setAddingLoading(false);
    }
  };

  const cancelAddFlow = () => {
    setShowLotSelector(false);
    setAddingError(null);
    setAddingLoading(false);
  };

  const openPaymentConfirmation = (invoice, paymentMethod) => {
    setPaymentConfirmation({ invoice, paymentMethod });
  };

  const executePayment = async (invoice, paymentMethod) => {
    const pmId = paymentMethod.id;
    setProcessingPaymentId(pmId);
    try {
      const payFn = httpsCallable(functions, "payInvoiceWithPaymentMethod");
      const result = await payFn({
        invoiceId: typeof invoice === "string" ? invoice : invoice.id,
        paymentMethodId: pmId,
      });
      if (result.data.success) {
        toast.success("Payment submitted. Thank you!", { autoClose: 4000 });
        setPaymentConfirmation(null);
        await fetchInvoices();
        await fetchPaymentMethods();
      } else {
        toast.error("Payment failed. Please try again or contact the HOA office.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      const msg = error?.message || "Failed to process payment. Please try again.";
      toast.error(msg);
    } finally {
      setProcessingPaymentId(null);
    }
  };

  const closePaymentConfirmation = () => {
    if (!processingPaymentId) setPaymentConfirmation(null);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const renderInvoiceCard = (invoice, { emphasize = false } = {}) => {
    const lot = findLotForInvoice(invoice, paymentMethodsByLot);
    const methods = paymentMethodsForInvoice(invoice, paymentMethodsByLot);
    const badge = dueBadge(invoice);
    const expanded = expandedId === invoice.id;
    const currency = invoice.currency || "usd";
    const amountDue = invoice.amount_remaining ?? invoice.amount_due;
    const statusClass = (invoice.displayStatus || invoice.status || "unknown").replace(/_/g, "-");

    return (
      <article
        key={invoice.id}
        className={`inv-card ${emphasize ? "inv-card--emphasize" : ""} ${isPaidInvoice(invoice) ? "inv-card--paid" : ""} ${isProcessingInvoice(invoice) ? "inv-card--processing" : ""}`}
      >
        <header className="inv-card__header">
          <div className="inv-card__title-block">
            <div className="inv-card__number">{invoice.number || invoice.id}</div>
            <div className="inv-card__lot">
              {invoice.lotNumber && invoice.lotNumber !== "N/A" ? `Lot ${invoice.lotNumber}` : "Lot"}
              {lot?.address ? ` · ${lot.address}` : invoice.lotAddress ? ` · ${invoice.lotAddress}` : ""}
            </div>
          </div>
          <div className="inv-card__amount-block">
            <div className="inv-card__amount">{formatMoney(amountDue, currency)}</div>
            <div className={`inv-status inv-status--${statusClass}`}>{invoiceStatusLabel(invoice)}</div>
          </div>
        </header>

        <div className="inv-card__meta-row">
          <div className="inv-meta-item">
            <span className="inv-meta-label">Issued</span>
            <span>{formatDate(invoice.created)}</span>
          </div>
          <div className="inv-meta-item">
            <span className="inv-meta-label">Due</span>
            <span>{formatDate(invoice.due_date)}</span>
          </div>
          {badge && (
            <div className={`inv-due-badge inv-due-badge--${badge.tone}`}>{badge.label}</div>
          )}
          {isPaidInvoice(invoice) && (
            <div className="inv-meta-item">
              <span className="inv-meta-label">Paid</span>
              <span>{formatDate(invoice.paid_at || invoice.created)}</span>
            </div>
          )}
        </div>

        {invoice.description && (
          <p className="inv-card__description">{invoice.description}</p>
        )}

        <p className="inv-card__help">{statusHelpText(invoice)}</p>

        <div className="inv-card__actions">
          {isOutstandingInvoice(invoice) && (
            methods.length > 0 ? (
              <div className="inv-pay-methods">
                <span className="inv-meta-label">Pay with saved method</span>
                <div className="inv-pay-pills">
                  {methods.map((pm) => (
                    <button
                      key={pm.id}
                      type="button"
                      className={`inv-pay-pill ${pm.isDefault ? "inv-pay-pill--default" : ""}`}
                      onClick={() => openPaymentConfirmation(invoice, pm)}
                      disabled={!!processingPaymentId}
                    >
                      {processingPaymentId === pm.id ? "Processing..." : pmLabel(pm)}
                      {pm.isDefault ? " · Default" : ""}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="inv-no-pm">
                No saved payment method for this lot.{" "}
                <button type="button" className="inv-link-btn" onClick={handleAddNewPaymentMethod}>
                  Add one
                </button>{" "}
                or pay on the secure invoice page.
              </div>
            )
          )}

          <div className="inv-links">
            {invoice.hosted_invoice_url && (
              <a
                href={invoice.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inv-btn inv-btn--primary"
              >
                View full invoice / pay on Stripe
              </a>
            )}
            {invoice.invoice_pdf && (
              <a
                href={invoice.invoice_pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inv-btn inv-btn--secondary"
              >
                {isPaidInvoice(invoice) ? "Download receipt (PDF)" : "Download PDF"}
              </a>
            )}
            <button type="button" className="inv-btn inv-btn--ghost" onClick={() => toggleExpand(invoice.id)}>
              {expanded ? "Hide details" : "Show full details"}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="inv-details">
            <h4 className="inv-details__title">Invoice details</h4>
            <dl className="inv-details__grid">
              <div>
                <dt>Invoice number</dt>
                <dd>{invoice.number || " - "}</dd>
              </div>
              <div>
                <dt>Stripe invoice ID</dt>
                <dd className="inv-mono">{invoice.id}</dd>
              </div>
              <div>
                <dt>Lot / tract</dt>
                <dd>{invoice.lotNumber || " - "}</dd>
              </div>
              <div>
                <dt>Billing name</dt>
                <dd>{invoice.ownerName || invoice.name1 || " - "}</dd>
              </div>
              <div>
                <dt>Billing email</dt>
                <dd>{invoice.customer_email || " - "}</dd>
              </div>
              <div>
                <dt>Property address</dt>
                <dd>{invoice.lotAddress || invoice.customer_address || " - "}</dd>
              </div>
              <div>
                <dt>Amount due</dt>
                <dd>{formatMoney(invoice.amount_due, currency)}</dd>
              </div>
              <div>
                <dt>Amount paid</dt>
                <dd>{formatMoney(invoice.amount_paid, currency)}</dd>
              </div>
              <div>
                <dt>Amount remaining</dt>
                <dd>{formatMoney(invoice.amount_remaining, currency)}</dd>
              </div>
              {invoice.subtotal != null && (
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatMoney(invoice.subtotal, currency)}</dd>
                </div>
              )}
              {invoice.tax != null && invoice.tax > 0 && (
                <div>
                  <dt>Tax</dt>
                  <dd>{formatMoney(invoice.tax, currency)}</dd>
                </div>
              )}
              {invoice.total != null && (
                <div>
                  <dt>Total</dt>
                  <dd>{formatMoney(invoice.total, currency)}</dd>
                </div>
              )}
              <div>
                <dt>Status (portal)</dt>
                <dd>{invoiceStatusLabel(invoice)}</dd>
              </div>
              <div>
                <dt>Status (Stripe)</dt>
                <dd>{invoice.stripeStatus || invoice.status || " - "}</dd>
              </div>
              <div>
                <dt>Payment status</dt>
                <dd>{invoice.paymentStatus || " - "}</dd>
              </div>
              <div>
                <dt>Payable online</dt>
                <dd>{invoice.isPayable === false ? "No" : "Yes"}</dd>
              </div>
              <div>
                <dt>Collection method</dt>
                <dd>
                  {invoice.collectionMethod === "charge_automatically"
                    ? "Automatic charge"
                    : invoice.collectionMethod === "send_invoice"
                      ? "Send invoice (pay manually)"
                      : invoice.collectionMethod || " - "}
                </dd>
              </div>
              {invoice.attemptCount != null && (
                <div>
                  <dt>Payment attempts</dt>
                  <dd>{invoice.attemptCount}</dd>
                </div>
              )}
              {invoice.nextPaymentAttempt && (
                <div>
                  <dt>Next automatic attempt</dt>
                  <dd>{formatDate(invoice.nextPaymentAttempt)}</dd>
                </div>
              )}
              {(invoice.period_start || invoice.period_end) && (
                <div>
                  <dt>Service period</dt>
                  <dd>
                    {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                  </dd>
                </div>
              )}
              {invoice.statementDescriptor && (
                <div>
                  <dt>Bank statement label</dt>
                  <dd>{invoice.statementDescriptor}</dd>
                </div>
              )}
              {invoice.memo && (
                <div className="inv-details__full">
                  <dt>Memo</dt>
                  <dd>{invoice.memo}</dd>
                </div>
              )}
              {invoice.footer && (
                <div className="inv-details__full">
                  <dt>Footer notes</dt>
                  <dd>{invoice.footer}</dd>
                </div>
              )}
            </dl>

            {Array.isArray(invoice.lines) && invoice.lines.length > 0 && (
              <div className="inv-lines">
                <h4 className="inv-details__title">Line items</h4>
                <table className="inv-lines-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((line) => (
                      <tr key={line.id || `${line.description}-${line.amount}`}>
                        <td>
                          {line.description}
                          {(line.periodStart || line.periodEnd) && (
                            <div className="inv-line-period">
                              {formatDate(line.periodStart)} - {formatDate(line.periodEnd)}
                            </div>
                          )}
                        </td>
                        <td>{line.quantity ?? 1}</td>
                        <td>{formatMoney(line.amount, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="inv-details__footnote">
              Billing is processed securely by Stripe on behalf of Nantahala Mountain Resort HOA.
              If something looks wrong, contact the HOA office with the invoice number above before paying again.
            </p>
          </div>
        )}
      </article>
    );
  };

  if (loading) {
    return <div className="invoices-loading">Loading your billing account...</div>;
  }

  return (
    <div className={`inv-page ${isMobile ? "inv-page--mobile" : ""}`}>
      <header className="inv-hero">
        <div>
          <p className="inv-eyebrow">Owner portal</p>
          <h1 className="inv-title">My invoices & payments</h1>
          <p className="inv-subtitle">
            Transparent view of what you owe, what you've paid, and how HOA billing works.
            Amounts come live from Stripe  -  the same system that generates official invoices.
          </p>
        </div>
        <button
          type="button"
          className="inv-btn inv-btn--secondary"
          onClick={refreshAll}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      {loadError && (
        <div className="inv-alert inv-alert--error" role="alert">
          <strong>Could not load invoices.</strong> {loadError}
          <button type="button" className="inv-link-btn" onClick={refreshAll}>
            Try again
          </button>
        </div>
      )}

      <section className="inv-summary" aria-label="Billing summary">
        <div className="inv-summary-card inv-summary-card--due">
          <div className="inv-summary-label">Outstanding balance</div>
          <div className="inv-summary-value">{formatMoney(totalOutstanding)}</div>
          <div className="inv-summary-hint">
            {outstanding.length === 0
              ? "Nothing due right now"
              : `${outstanding.length} open invoice${outstanding.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="inv-summary-card">
          <div className="inv-summary-label">Payments processing</div>
          <div className="inv-summary-value">{formatMoney(totalProcessing)}</div>
          <div className="inv-summary-hint">
            {processing.length === 0
              ? "No pending clearances"
              : `${processing.length} awaiting bank/card clearance`}
          </div>
        </div>
        <div className="inv-summary-card inv-summary-card--paid">
          <div className="inv-summary-label">Paid (shown)</div>
          <div className="inv-summary-value">{formatMoney(totalPaid)}</div>
          <div className="inv-summary-hint">{paid.length} paid invoice{paid.length === 1 ? "" : "s"}</div>
        </div>
        <div className="inv-summary-card">
          <div className="inv-summary-label">Next due date</div>
          <div className="inv-summary-value inv-summary-value--sm">
            {nextDue ? formatDate(nextDue.due_date) : " - "}
          </div>
          <div className="inv-summary-hint">
            {nextDue
              ? `${nextDue.number || "Invoice"} · ${formatMoney(nextDue.amount_remaining ?? nextDue.amount_due, nextDue.currency)}`
              : "No upcoming due date"}
          </div>
        </div>
      </section>

      <section className="inv-transparency">
        <h2>How HOA billing works</h2>
        <ul>
          <li>
            <strong>Official invoices</strong> are created by the HOA and hosted by Stripe. The PDF and
            "View full invoice" links are the same documents the HOA uses for records.
          </li>
          <li>
            <strong>Saved cards and bank accounts</strong> are stored per lot. Paying here charges that
            method immediately (cards) or starts an ACH debit (banks, often 3-5 business days).
          </li>
          <li>
            <strong>Processing</strong> means your payment was submitted and is not fully settled yet  - 
            please wait for it to clear before paying again.
          </li>
          <li>
            <strong>Questions?</strong> Contact the HOA office with the invoice number. Do not ignore
            overdue balances; late fees may apply per association policy.
          </li>
        </ul>
      </section>

      {processing.length > 0 && (
        <section className="inv-section">
          <div className="inv-section-header">
            <h2>Payments processing</h2>
            <span className="inv-section-count">{processing.length}</span>
          </div>
          <p className="inv-section-lead">
            These payments were already submitted. Bank transfers can take several business days to post.
          </p>
          <div className="inv-card-list">{processing.map((inv) => renderInvoiceCard(inv))}</div>
        </section>
      )}

      <section className="inv-section">
        <div className="inv-section-header">
          <h2>Outstanding invoices</h2>
          <span className="inv-section-count">{outstanding.length}</span>
        </div>
        {outstanding.length === 0 ? (
          <div className="inv-empty">
            <strong>You're all caught up.</strong>
            <p>There are no open balances on your owner account right now.</p>
          </div>
        ) : (
          <div className="inv-card-list">
            {outstanding
              .slice()
              .sort((a, b) => (a.due_date || 0) - (b.due_date || 0))
              .map((inv) => renderInvoiceCard(inv, { emphasize: true }))}
          </div>
        )}
      </section>

      <section className="inv-section">
        <div className="inv-section-header">
          <h2>Payment methods</h2>
          <button
            type="button"
            className="inv-btn inv-btn--primary"
            onClick={handleAddNewPaymentMethod}
            disabled={addingLoading}
          >
            {addingLoading ? "Redirecting to Stripe..." : "+ Add payment method"}
          </button>
        </div>
        <p className="inv-section-lead">
          Methods are saved <strong>per lot</strong> for security and correct billing. The same card can
          appear under multiple lots if you added it separately for each.
        </p>

        {showLotSelector && (
          <div className="inv-lot-chooser">
            <p>
              <strong>Which lot should this payment method be saved for?</strong>
            </p>
            <p className="inv-section-lead">You'll be redirected to Stripe's secure page to enter card or bank details.</p>
            <div className="inv-lot-chooser-buttons">
              {paymentMethodsByLot.map((lot) => (
                <button
                  key={lot.lotId}
                  type="button"
                  className="inv-btn inv-btn--secondary"
                  onClick={() => startAddPaymentMethodForLot(lot.lotId)}
                  disabled={addingLoading}
                >
                  {lot.address || "Lot"} {lot.tract ? `(${lot.tract})` : ""}
                </button>
              ))}
            </div>
            <button type="button" className="inv-link-btn" onClick={cancelAddFlow} disabled={addingLoading}>
              Cancel
            </button>
          </div>
        )}

        {addingError && !showLotSelector && <p className="inv-alert inv-alert--error">{addingError}</p>}
        {addingLoading && !showLotSelector && (
          <p className="inv-section-lead">Redirecting you to Stripe's secure checkout...</p>
        )}

        {loadingPaymentMethods ? (
          <p className="inv-section-lead">Loading saved payment methods...</p>
        ) : paymentMethodsByLot.length === 0 ? (
          <div className="inv-empty">
            <p>No lots or payment methods on file yet. Contact the HOA if your property should appear here.</p>
          </div>
        ) : (
          paymentMethodsByLot.map((lot) => (
            <div key={lot.lotId} className="inv-lot-pm">
              <h3>
                {lot.address || "Property"} {lot.tract ? <span className="inv-lot-tag">Lot {lot.tract}</span> : null}
              </h3>
              {lot.paymentMethods?.length ? (
                <div className="inv-pm-grid">
                  {lot.paymentMethods.map((pm) => (
                    <div key={pm.id} className="inv-pm-card">
                      <div className="inv-pm-main">
                        <strong>{pmLabel(pm)}</strong>
                        {pm.type === "card" && (
                          <div className="inv-pm-sub">
                            Expires {pm.exp_month}/{pm.exp_year}
                          </div>
                        )}
                        {pm.type === "us_bank_account" && (
                          <div className="inv-pm-sub">{pm.account_type || "Bank account"}</div>
                        )}
                      </div>
                      {pm.isDefault && <span className="inv-default-badge">Default</span>}
                      <div className="inv-pm-actions">
                        {!pm.isDefault && (
                          <button type="button" onClick={() => handleSetDefault(lot.lotId, pm.id)}>
                            Set default
                          </button>
                        )}
                        <button type="button" className="danger" onClick={() => handleRemovePaymentMethod(pm.id)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="inv-section-lead">No payment methods saved for this lot yet.</p>
              )}
            </div>
          ))
        )}
      </section>

      <section className="inv-section">
        <div className="inv-section-header">
          <h2>Invoice history</h2>
          <div className="inv-filter-tabs" role="tablist" aria-label="History filter">
            {[
              { id: "paid", label: "Paid" },
              { id: "all", label: "All" },
              { id: "void", label: "Void" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={historyFilter === tab.id}
                className={`inv-filter-tab ${historyFilter === tab.id ? "is-active" : ""}`}
                onClick={() => setHistoryFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {historyList.length === 0 ? (
          <div className="inv-empty">
            <p>No invoices in this view yet.</p>
          </div>
        ) : (
          <div className="inv-card-list">{historyList.map((inv) => renderInvoiceCard(inv))}</div>
        )}
      </section>

      {paymentConfirmation && (
        <div className="inv-modal-backdrop" onClick={closePaymentConfirmation} role="presentation">
          <div
            className="inv-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inv-pay-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="inv-pay-title">Confirm payment</h3>
            <div className="inv-modal-body">
              <div>
                <span className="inv-meta-label">Invoice</span>
                <div>{paymentConfirmation.invoice.number || paymentConfirmation.invoice.id}</div>
              </div>
              <div>
                <span className="inv-meta-label">Lot</span>
                <div>{paymentConfirmation.invoice.lotNumber || " - "}</div>
              </div>
              <div>
                <span className="inv-meta-label">Amount to charge</span>
                <div className="inv-modal-amount">
                  {formatMoney(
                    paymentConfirmation.invoice.amount_remaining ?? paymentConfirmation.invoice.amount_due,
                    paymentConfirmation.invoice.currency,
                  )}
                </div>
              </div>
              <div>
                <span className="inv-meta-label">Payment method</span>
                <div>{pmLabel(paymentConfirmation.paymentMethod)}</div>
              </div>
            </div>
            <p className="inv-modal-note">
              {paymentConfirmation.paymentMethod.type === "us_bank_account"
                ? "Bank payments are submitted now and usually clear in 3-5 business days. You may see a pending charge on your account."
                : "Your card will be charged immediately through Stripe."}
            </p>
            <div className="inv-modal-actions">
              <button
                type="button"
                className="inv-btn inv-btn--ghost"
                onClick={closePaymentConfirmation}
                disabled={!!processingPaymentId}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inv-btn inv-btn--primary"
                onClick={() =>
                  executePayment(paymentConfirmation.invoice, paymentConfirmation.paymentMethod)
                }
                disabled={!!processingPaymentId}
              >
                {processingPaymentId ? "Processing..." : "Confirm & pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
